import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { AppHeader } from "../components/AppHeader";
import { Button, Field } from "../components/UIKit";
import { AppleButton } from "../components/AppleButton";

function GoogleButton({ onClick, testID }) {
  return (
    <button
      onClick={onClick}
      data-testid={testID}
      style={{
        width: "100%", display: "inline-flex", alignItems: "center",
        justifyContent: "center", gap: 10,
        padding: "12px 22px", borderRadius: 999,
        background: "#fff", color: "var(--mf-text)",
        border: "1px solid var(--mf-border)",
        fontSize: 15, fontWeight: 600, cursor: "pointer",
        transition: "background 150ms",
        marginBottom: 12,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--mf-bg-muted)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
    >
      <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.4 34.6 26.9 36 24 36c-5.3 0-9.7-3.4-11.3-8.1l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.6 5l6.2 5.3C40.9 34.8 44 29.8 44 24c0-1.2-.1-2.4-.4-3.5z"/>
      </svg>
      Continuer avec Google
    </button>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWithGoogle, signInWithApple } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Post-deletion breadcrumb: /login?deleted=1 shows a discreet success toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("deleted") === "1") {
      toast.success("Votre compte a été supprimé.");
      // Clean the query string so a refresh doesn't re-toast.
      try {
        window.history.replaceState(null, "", location.pathname);
      } catch { /* ignore */ }
    }
  }, []);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError(null);
    if (!email || !password) {
      setError("Email et mot de passe requis");
      return;
    }
    setLoading(true);
    try {
      await signIn(email.trim().toLowerCase(), password);
      toast.success("Content de vous revoir 👋");
      navigate("/", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || "Email ou mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mf-screen" style={{
      background: "var(--mf-bg)",
      paddingTop: "env(safe-area-inset-top)",
    }}>
      <AppHeader background="surface" clickable={false} />
      <div style={{
        flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "16px 24px calc(24px + env(safe-area-inset-bottom))",
        width: "100%",
      }}>
      <div style={{ width: "100%", maxWidth: 440 }} className="mf-anim-in">
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            fontSize: 15, color: "var(--mf-text-secondary)", letterSpacing: 0.2,
          }}>
            Votre flotte, sans effort.
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="login-form">
          <AppleButton
            testID="login-apple-button"
            onClick={async () => {
              try {
                await signInWithApple();
                toast.success("Connexion réussie");
              } catch (e) {
                toast.error(e?.message || "Connexion Apple impossible.");
              }
            }}
          />
          <GoogleButton testID="login-google-button" onClick={signInWithGoogle} />
          <div style={{ position: "relative", margin: "18px 0", textAlign: "center" }}>
            <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "var(--mf-border)" }} />
            <span style={{
              position: "relative", background: "var(--mf-bg)",
              padding: "0 12px", color: "var(--mf-text-muted)",
              fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8,
            }}>ou</span>
          </div>
          <Field
            testID="login-email-input"
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="vous@email.com"
            autoComplete="email"
          />
          <Field
            testID="login-password-input"
            label="Mot de passe"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            autoComplete="current-password"
            error={error}
          />
          <Button testID="login-submit-button" type="submit" loading={loading}>
            Se connecter
          </Button>
        </form>

        <div style={{ textAlign: "center", marginTop: 14 }}>
          <Link
            to="/forgot-password"
            data-testid="login-goto-forgot-password"
            style={{ color: "var(--mf-text-secondary)", fontSize: 14, textDecoration: "none" }}
          >
            Mot de passe oublié ?
          </Link>
        </div>

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <Link
            to="/signup"
            data-testid="login-goto-signup"
            style={{ color: "var(--mf-text-secondary)", fontSize: 14, textDecoration: "none" }}
          >
            Pas encore de compte ?{" "}
            <span style={{ color: "var(--mf-brand)", fontWeight: 700 }}>Créer un compte</span>
          </Link>
        </div>

        <LegalLinksFooter testIdPrefix="login" />
      </div>
      </div>
    </div>
  );
}

export function Signup() {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle, signInWithApple } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (loading) return;
    setError(null);
    if (!email || !password) return setError("Email et mot de passe requis");
    if (password.length < 8) return setError("Mot de passe : 8 caractères minimum");
    setLoading(true);
    try {
      const trimmedName = name.trim();
      await signUp(email.trim().toLowerCase(), password, trimmedName || undefined);
      toast.success(trimmedName ? `Bienvenue, ${trimmedName.split(" ")[0]} 🎉` : "Bienvenue sur My Fleet 🎉");
      navigate("/", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Inscription impossible. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mf-screen" style={{
      background: "var(--mf-bg)",
      paddingTop: "env(safe-area-inset-top)",
    }}>
      <AppHeader background="surface" clickable={false} />
      <div style={{
        flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "16px 24px calc(24px + env(safe-area-inset-bottom))",
        width: "100%",
      }}>
      <div style={{ width: "100%", maxWidth: 440 }} className="mf-anim-in">
        <div style={{ marginBottom: 20, textAlign: "center" }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.4 }}>
            Créer un compte
          </h2>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--mf-text-secondary)" }}>
            Commencez à gérer votre flotte en quelques secondes
          </p>
        </div>

        <form onSubmit={onSubmit} data-testid="signup-form">
          <AppleButton
            testID="signup-apple-button"
            onClick={async () => {
              try {
                await signInWithApple();
                toast.success("Bienvenue sur My Fleet 🎉");
              } catch (e) {
                toast.error(e?.message || "Inscription Apple impossible.");
              }
            }}
          />
          <GoogleButton testID="signup-google-button" onClick={signInWithGoogle} />
          <div style={{ position: "relative", margin: "18px 0", textAlign: "center" }}>
            <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: "var(--mf-border)" }} />
            <span style={{
              position: "relative", background: "var(--mf-bg)",
              padding: "0 12px", color: "var(--mf-text-muted)",
              fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8,
            }}>ou</span>
          </div>
          <Field testID="signup-name-input" label="Nom (optionnel)" value={name} onChange={setName} placeholder="Votre nom" />
          <Field testID="signup-email-input" label="Email" type="email" value={email} onChange={setEmail} placeholder="vous@email.com" autoComplete="email" />
          <Field testID="signup-password-input" label="Mot de passe" type="password" value={password} onChange={setPassword} placeholder="8 caractères minimum" autoComplete="new-password" error={error} />
          <Button testID="signup-submit-button" type="submit" loading={loading}>Créer mon compte</Button>
          <p
            data-testid="signup-terms-mention"
            style={{
              margin: "12px 0 0",
              fontSize: 12,
              color: "var(--mf-text-muted)",
              textAlign: "center",
              lineHeight: 1.5,
            }}
          >
            En créant un compte, vous acceptez nos{" "}
            <Link to="/terms" data-testid="signup-terms-link" style={{ color: "var(--mf-text-secondary)", textDecoration: "underline" }}>
              Conditions d&apos;utilisation
            </Link>{" "}
            et reconnaissez avoir lu la{" "}
            <Link to="/privacy" data-testid="signup-privacy-link" style={{ color: "var(--mf-text-secondary)", textDecoration: "underline" }}>
              Politique de confidentialité
            </Link>
            .
          </p>
        </form>

        <div style={{ textAlign: "center", marginTop: 20 }}>
          <Link
            to="/login"
            data-testid="signup-goto-login"
            style={{ color: "var(--mf-text-secondary)", fontSize: 14, textDecoration: "none" }}
          >
            Déjà inscrit ?{" "}
            <span style={{ color: "var(--mf-brand)", fontWeight: 700 }}>Se connecter</span>
          </Link>
        </div>

        <LegalLinksFooter testIdPrefix="signup" />
      </div>
      </div>
    </div>
  );
}

// Small legal-links row shown at the bottom of both Login and Signup forms.
// Kept intentionally discreet (small, muted, single line on desktop) — not a
// full footer, not a banner.
function LegalLinksFooter({ testIdPrefix }) {
  const linkStyle = {
    color: "var(--mf-text-muted)",
    fontSize: 12,
    textDecoration: "none",
    padding: "4px 2px",
  };
  return (
    <div
      data-testid={`${testIdPrefix}-legal-footer`}
      style={{
        marginTop: 28,
        display: "flex",
        flexWrap: "wrap",
        gap: "6px 12px",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: 16,
        borderTop: "1px solid var(--mf-divider)",
      }}
    >
      <Link to="/privacy" data-testid={`${testIdPrefix}-legal-privacy`} style={linkStyle}>
        Confidentialité
      </Link>
      <span aria-hidden="true" style={{ color: "var(--mf-text-muted)", fontSize: 11 }}>·</span>
      <Link to="/terms" data-testid={`${testIdPrefix}-legal-terms`} style={linkStyle}>
        Conditions
      </Link>
      <span aria-hidden="true" style={{ color: "var(--mf-text-muted)", fontSize: 11 }}>·</span>
      <Link to="/legal" data-testid={`${testIdPrefix}-legal-legal`} style={linkStyle}>
        Mentions légales
      </Link>
    </div>
  );
}
